import { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import { Link as RouterLink, useLocation, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EmbraceLogo from "../Common/EmbraceLogo";
import { AppStoreIcon, GooglePlayIcon } from "../Common/assets/Icons";
import StoreDownloadBadge from "./StoreDownloadBadge";
import {
  isMobileDevice,
  isAndroid,
  buildMahaloDeepLink,
} from "./mobileDeviceUtils";

const AUTO_REDIRECT_DELAY_MS = 500;

const storeLinks = {
  googlePlayLink: import.meta.env.REACT_APP_ANDROID_STORE_URL?.trim() ?? "",
  appStoreLink: import.meta.env.REACT_APP_IOS_STORE_URL?.trim() ?? "",
};

const hasStoreLinks = Boolean(
  storeLinks.appStoreLink || storeLinks.googlePlayLink,
);

type LandingVariant = "generic" | "survey" | "consent" | "todo";

const MobileAppLanding = () => {
  const { t } = useTranslation("mobile-landing");
  const { pathname, search } = useLocation();
  const intentLinkRef = useRef<HTMLAnchorElement>(null);
  const surveyMatch = useMatch("/mobile/survey");
  const consentMatch = useMatch("/mobile/consent");
  const todoMatch = useMatch("/mobile/todo");

  const variant: LandingVariant = surveyMatch
    ? "survey"
    : consentMatch
      ? "consent"
      : todoMatch
        ? "todo"
        : "generic";

  const mobile = useMemo(() => isMobileDevice(), []);
  const android = useMemo(() => isAndroid(), []);
  const deepLink = useMemo(
    () => buildMahaloDeepLink(pathname, search),
    [pathname, search],
  );

  const packageName = useMemo(() => {
    const param = new URLSearchParams(search).get("packageName");
    return param?.trim() || null;
  }, [search]);

  // Auto-redirect on both iOS and Android using the custom scheme.
  // The button below serves as a fallback if the auto-redirect doesn't fire.
  useEffect(() => {
    if (!mobile) return;

    const timer = setTimeout(() => {
      window.location.href = deepLink;
    }, AUTO_REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [mobile, deepLink]);

  // Android: auto-redirect using a hidden iframe — Chrome's intent resolver
  // can pick up intent:// URLs from iframe src even without a user gesture.
  useEffect(() => {
    if (!mobile || !android) return;

    const timer = setTimeout(() => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = deepLink;
      document.body.appendChild(iframe);

      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 2000);
    }, AUTO_REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [mobile, android, deepLink]);

  const { Icon, headline, subheadline } = useMemo(() => {
    if (variant === "survey") {
      return {
        Icon: AssignmentOutlinedIcon,
        headline: t("SURVEY_HEADLINE"),
        subheadline: packageName
          ? t("SURVEY_SUBHEADLINE_NAMED", { packageName })
          : t("SURVEY_SUBHEADLINE"),
      };
    }
    if (variant === "consent") {
      return {
        Icon: DescriptionOutlinedIcon,
        headline: t("CONSENT_HEADLINE"),
        subheadline: t("CONSENT_SUBHEADLINE"),
      };
    }
    if (variant === "todo") {
      return {
        Icon: PlaylistAddCheckOutlinedIcon,
        headline: t("TODO_HEADLINE"),
        subheadline: t("TODO_SUBHEADLINE"),
      };
    }
    return {
      Icon: SmartphoneOutlinedIcon,
      headline: t("GENERIC_HEADLINE"),
      subheadline: t("GENERIC_SUBHEADLINE"),
    };
  }, [variant, packageName, t]);

  const handleOpenApp = () => {
    if (android && intentLinkRef.current) {
      intentLinkRef.current.click();
      return;
    }
    window.location.href = deepLink;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(165deg, #F4F5F7 0%, #EDEBFE 45%, #F9FAFB 100%)",
        px: 2,
        py: 3,
      }}
    >
      {/* Hidden anchor for Android — native <a> is the most reliable way to
          trigger intent:// resolution on Chrome from a user gesture */}
      {android && (
        <a
          ref={intentLinkRef}
          href={deepLink}
          style={{ display: "none" }}
          aria-hidden="true"
        >
          open
        </a>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <EmbraceLogo sx={{ height: { xs: 44, md: 48 } }} />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            bgcolor: "#FFFFFF",
            borderRadius: "24px",
            boxShadow:
              "0 4px 6px -1px rgba(0,0,0,0.08), 0 20px 40px -12px rgba(126, 58, 242, 0.15)",
            px: { xs: 3, sm: 4 },
            py: { xs: 4, sm: 5 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "20px",
              bgcolor: "secondary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "primary.main" }} />
          </Box>

          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
              fontWeight: 700,
              color: "text.primary",
              mb: 1.5,
              lineHeight: 1.25,
            }}
          >
            {headline}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.95rem", sm: "1rem" },
              lineHeight: 1.6,
              mb: mobile ? 3 : 4,
            }}
          >
            {subheadline}
          </Typography>

          {mobile && android && (
            <Button
              component="a"
              href={deepLink}
              variant="contained"
              size="large"
              startIcon={<OpenInNewIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                width: "100%",
                mb: 3,
                textDecoration: "none",
              }}
            >
              {t("OPEN_IN_APP")}
            </Button>
          )}

          {mobile && !android && (
            <Button
              variant="contained"
              size="large"
              onClick={handleOpenApp}
              startIcon={<OpenInNewIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                width: "100%",
                mb: 3,
              }}
            >
              {t("OPEN_IN_APP")}
            </Button>
          )}

          {!mobile && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              {t("DESKTOP_NOTE")}
            </Typography>
          )}

          <Stack spacing={1.5} alignItems="center">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
            >
              <StoreDownloadBadge
                href={storeLinks.googlePlayLink || undefined}
                ariaLabel={t("GOOGLE_PLAY_ARIA")}
                icon={<GooglePlayIcon />}
                topLine={t("GOOGLE_PLAY_TOP")}
                bottomLine={t("GOOGLE_PLAY_BOTTOM")}
              />
              <StoreDownloadBadge
                href={storeLinks.appStoreLink || undefined}
                ariaLabel={t("APP_STORE_ARIA")}
                icon={<AppStoreIcon />}
                topLine={t("APP_STORE_TOP")}
                bottomLine={t("APP_STORE_BOTTOM")}
              />
            </Stack>
            {!hasStoreLinks ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}
              >
                {t("STORE_LINKS_SOON")}
              </Typography>
            ) : null}
          </Stack>

          {mobile && (
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                color: "text.secondary",
                fontSize: "0.8125rem",
              }}
            >
              {t("ALREADY_HAVE_APP")}
            </Typography>
          )}
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 4, flexWrap: "wrap" }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          fontSize="0.8125rem"
        >
          {t("FOOTER_CONTACT")}
        </Typography>
        <Link
          component={RouterLink}
          to="/privacy-policy"
          underline="hover"
          color="primary"
          fontSize="0.8125rem"
        >
          {t("PRIVACY_POLICY")}
        </Link>
      </Stack>
    </Box>
  );
};

export default MobileAppLanding;
